import {
  useState,
  useEffect,
  Dispatch,
  SetStateAction,
} from 'react';
import {
  useLocation,
  useNavigate,
} from 'react-router-dom';
import isNil from 'lodash/isNil';
import isString from 'lodash/isString';
import {
  notEmptyString,
} from '@populateui/utils';
import {
  patchSearchParams,
} from 'utils/urlWriter';

function getSessionStorageValue(storageKey: string): string | null {
  let storedValue: unknown = null;

  try {
    storedValue = JSON.parse(sessionStorage.getItem(storageKey) ?? 'null');
  } catch {
    storedValue = null;
  }

  return isString(storedValue) ? storedValue : null;
}

function setSessionStorageValue(storageKey: string, value: string | null) {
  if (isNil(value)) {
    sessionStorage.removeItem(storageKey);
  } else {
    sessionStorage.setItem(storageKey, JSON.stringify(value));
  }
}

export default function useUrlSyncState<T>(
  defaultValue: T,
  key: string,
  parseState: (val: T) => string,
  parseQuery: (val: string) => T,
  sessionStorageKey?: string,
): [T, Dispatch<SetStateAction<T>>] {
  const navigate = useNavigate();
  const { search = '' } = useLocation();
  const urlParam = new URLSearchParams(search).get(key);

  const [value, dispatcher] = useState<T>(() => {
    const storageValue = isNil(urlParam) && !isNil(sessionStorageKey)
      ? getSessionStorageValue(sessionStorageKey)
      : null;

    return parseQuery(urlParam ?? storageValue ?? parseState(defaultValue));
  });

  const toParam = (val: T) => {
    const serialized = isNil(val) ? null : parseState(val);
    return notEmptyString(serialized) ? serialized : null;
  };

  // Follow URL changes made outside this hook (back/forward, same-route links)
  useEffect(() => {
    if (isNil(urlParam)) {
      // Param removed externally: restore it from the current value
      const stringValue = toParam(value);
      if (!isNil(stringValue)) {
        patchSearchParams({ [key]: stringValue }, navigate);
      }
      return;
    }
    if (urlParam !== parseState(value)) {
      dispatcher(parseQuery(urlParam));
    }
  }, [urlParam]);

  useEffect(() => {
    const stringValue = toParam(value);
    patchSearchParams({ [key]: stringValue }, navigate);

    if (!isNil(sessionStorageKey)) {
      setSessionStorageValue(sessionStorageKey, stringValue);
    }
  }, [value]);

  return [value, dispatcher];
}
